import { inject, resource } from "@angular/core";
import { signalStore, withComputed, withMethods, withProps } from "@ngrx/signals";
import { CacheRepository } from "../decorators/cache.decorator";
import { FlsService } from "../services/fls.service";
import { AccessLevel, AccessLevels, Fls, hasEditFls, hasFullFls, hasReadFls, isMinFls } from "../pojo/Fls";
import { S25Util } from "../util/s25-util";
import { LoginStore } from "./login.store";

export const FlsStore = signalStore(
    { providedIn: "root" },
    withProps((store) => ({
        _loginStore: inject(LoginStore),
    })),
    withProps((store) => ({
        _resource: resource({
            params: () => store._loginStore.user()?.userId ?? false, // Refresh when user ID changes
            loader: () => FlsService.getFls(),
        }),
    })),
    withComputed((store) => ({
        value: () => (store._resource.hasValue() && store._resource.value()) || ({} as Fls),
        loaded: () => store._resource.hasValue(),
    })),
    withMethods((store) => ({
        async ensureLoaded(): Promise<void> {
            if (store.loaded()) {
                return;
            }
            store._resource.reload();
            await S25Util.waitFor(() => store.loaded(), 50);
        },
        accessLevel: (key: keyof Fls) => store.value()[key] ?? AccessLevels.None,
    })),
    withMethods((store) => ({
        refresh: () => {
            CacheRepository.invalidateFunction(FlsService.getFls);
            return store._resource.reload();
        },
        invalidate: () => {
            CacheRepository.invalidateFunction(FlsService.getFls);
            store._resource.set(undefined);
            return store._resource.reload();
        },
    })),
    withMethods((store) => ({
        hasRead: (key: keyof Fls): boolean => hasReadFls(store.accessLevel(key)),
        hasEdit: (key: keyof Fls): boolean => hasEditFls(store.accessLevel(key)),
        hasFull: (key: keyof Fls): boolean => hasFullFls(store.accessLevel(key)),
        hasAtLeast: (key: keyof Fls, level: AccessLevel): boolean => isMinFls(store.accessLevel(key), level),
    })),
    withMethods((store) => ({
        canOverrideBlockedBy: () => store.hasFull("SPACE_PERM") || store.hasFull("SPACE_BLOCKED"),
    })),
);
export type FlsStore = InstanceType<typeof FlsStore>;
